/**
 * Maps (SC-422): per-map product settings, through Cloud Code. The map files themselves stay in
 * `tilesets.json`.
 */
import { parseCloudRun } from '../parseCloud'
import type { MapSettings, TilePoint } from '../../../../types/Maps'
import { createListMemo } from './listMemo'

// The enabled-maps list feeds the room, lobby and booking pickers; it changes only when a
// platform admin edits a map, which clears it (SC-477).
const enabledMapsMemo = createListMemo()

/** A `Map` row as `listMaps` returns it, with its reception. */
export interface MapRecord extends MapSettings {
  hasReception: boolean
  receptionVirtualRoomId: string | null
}

/** What every signed-in user may see: enough for the room, lobby and booking pickers. */
export interface EnabledMap {
  mapId: string
  name: string
  /** Shown on the map card at company creation (SC-438). Empty when not set. */
  description: string
  thumbnailUrl: string
  playerStart: TilePoint
  isLobbyMap: boolean
  hasReception: boolean
}

export interface RegisterMapInput {
  mapId: string
  name: string
  playerStart: TilePoint
  width: number
  height: number
  enabled?: boolean
  autoSeatOnJoin?: boolean
}

export type MapChanges = Partial<
  Pick<MapSettings, 'name' | 'description' | 'thumbnailUrl' | 'enabled' | 'playerStart' | 'isLobbyMap' | 'autoSeatOnJoin'>
>

export const MapService = {
  /** Memoised for 60 s and shared between concurrent callers; `fresh` skips the memo. */
  listEnabled(options?: { fresh?: boolean }): Promise<EnabledMap[]> {
    return enabledMapsMemo.get('enabled', () => parseCloudRun<EnabledMap[]>('listEnabledMaps'), options)
  },
  listAll(): Promise<MapRecord[]> {
    return parseCloudRun<MapRecord[]>('listMaps')
  },
  register(input: RegisterMapInput): Promise<MapRecord> {
    return enabledMapsMemo.afterWrite(parseCloudRun<MapRecord>('registerMap', { ...input }))
  },
  update(mapId: string, changes: MapChanges): Promise<MapRecord> {
    return enabledMapsMemo.afterWrite(parseCloudRun<MapRecord>('updateMap', { mapId, changes: { ...changes } }))
  },
  setReception(mapId: string, virtualRoomId: string | null): Promise<{ mapId: string; receptionVirtualRoomId: string | null }> {
    return enabledMapsMemo.afterWrite(parseCloudRun('setMapReception', { mapId, virtualRoomId }))
  },
}

export default MapService
